<script setup lang="ts">
import { ref } from 'vue'

import type { CapabilityProfile } from '../types/callables'
import BaseDialog from '../ui/components/BaseDialog.vue'
import { useDialog } from '../ui/dialogs'

const props = defineProps<{
  profiles:       CapabilityProfile[]
  /** Pre-select this id if it's still in `profiles`. */
  defaultProfile: string | null
  /**
   * True at boot when the user hasn't picked yet and must — dismissing would
   * leave <cad-sketch> uninitialised. The caller also passes
   * { dismissible: false } to openDialog so backdrop/Escape are inert.
   * False for a mid-session switch, where cancelling just returns to the
   * current session unchanged.
   */
  required: boolean
  /**
   * Async work that must complete before the dialog closes — a license fetch
   * for boot, or a page reload for a mid-session switch. Dialog shows
   * "Loading…" on the primary button while this runs, then closes with the
   * selected id whether onSelect resolves or throws (caller handles failure).
   */
  onSelect: (profileId: string) => Promise<void>
}>()

const { close } = useDialog<string>()

// Pre-select the default when it's still in the list; otherwise the first
// profile — pushes the user's next click straight to Confirm rather than a
// two-click flow.
const initialSelection = props.defaultProfile && props.profiles.some((p) => p.id === props.defaultProfile)
  ? props.defaultProfile
  : (props.profiles[0]?.id ?? null)

const selected = ref<string | null>(initialSelection)
const busy     = ref(false)

async function onSubmit() {
  if (!selected.value) return
  const chosen = selected.value
  busy.value = true
  try {
    await props.onSelect(chosen)
  } finally {
    close(chosen)
  }
}
</script>

<template>
  <BaseDialog
    :title="required ? 'Choose a profile to start' : 'Switch profile'"
    size="wide"
    @dismiss="close()"
  >
    <p class="hint">
      Your account has more than one capability profile. The one you pick
      stays active until you sign out or switch again.
    </p>

    <p v-if="!required" class="switch-warn" role="alert">
      <strong>Save your drawing first.</strong>
      Switching profiles reloads the editor — any unsaved changes will be lost.
    </p>

    <form id="profile-picker-form" @submit.prevent="onSubmit">
      <fieldset :disabled="busy">
        <legend>Available profiles</legend>
        <label
          v-for="p in profiles"
          :key="p.id"
          class="row"
          :class="{ 'is-selected': selected === p.id }"
        >
          <input
            type="radio"
            class="radio"
            name="profile-choice"
            :value="p.id"
            :checked="selected === p.id"
            @change="selected = p.id"
          />
          <span class="body">
            <span class="name">
              {{ p.name }}
              <span v-if="p.id === defaultProfile" class="default-tag">default</span>
            </span>
            <span v-if="p.description" class="desc">{{ p.description }}</span>
            <span class="caps">
              <span v-if="p.capabilities.list.length">
                {{ p.capabilities.mode === 'denyList' ? 'Denies' : 'Allows' }}:
                <code v-for="cap in p.capabilities.list" :key="cap">{{ cap }}</code>
              </span>
              <span v-else class="empty">No capabilities listed.</span>
            </span>
          </span>
        </label>
      </fieldset>
    </form>

    <template #actions>
      <button
        v-if="!required"
        type="button"
        class="btn btn-secondary"
        :disabled="busy"
        @click="close()"
      >Cancel</button>
      <button
        type="submit"
        form="profile-picker-form"
        class="btn btn-primary"
        :disabled="busy || !selected"
      >
        {{ busy ? 'Loading…' : (required ? 'Start' : 'Switch') }}
      </button>
    </template>
  </BaseDialog>
</template>

<style scoped>
fieldset {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

.row {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid transparent;
  border-radius: var(--radius);
  cursor: pointer;
}

.row:hover { background: var(--bg-subtle); }

.row.is-selected {
  border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
  background:   color-mix(in srgb, var(--accent) 8%, transparent);
}

.row input[type='radio'] { margin-top: 0.25rem; }

.body { display: flex; flex-direction: column; gap: 0.2rem; }

.name {
  font-size: 0.92rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
}

.desc {
  font-size: 0.82rem;
  color: var(--fg-muted);
  line-height: 1.4;
}

.caps { font-size: 0.78rem; color: var(--fg-muted); }

.caps code {
  display: inline-block;
  padding: 0.05rem 0.35rem;
  margin: 0.1rem 0.25rem 0.1rem 0;
  border-radius: 3px;
  background: var(--bg-subtle);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.75rem;
}

.empty { font-style: italic; }

.default-tag {
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent) 18%, transparent);
  color: var(--fg);
  font-size: 0.7rem;
  font-weight: 500;
}

.switch-warn {
  margin: 0;
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--radius);
  border: 1px solid color-mix(in srgb, var(--danger) 35%, var(--border));
  background: color-mix(in srgb, var(--danger) 10%, transparent);
  color: var(--fg);
  font-size: 0.85rem;
  line-height: 1.45;
}

.switch-warn strong { color: var(--danger); }
</style>
